import { TwinInstruction, TwinInstructionType } from 'api/model'
import { useActiveOrganization } from './use-organization'
import { useLayoutEffect, useState } from 'react'
import { twinsListInstructions, twinsSuggestedQuestions } from 'api/endpoints/jelledFromFileSpecWithTransformer'
import { useRecoilState } from 'recoil'
import { instructionsAtom, questionTypeInstructionsAtom } from 'state/instructions'

export const SUMMARIZE_EMAIL_SUGGESTION = 'Summarize my emails from today'
export const CHECK_CALENDAR_SUGGESTION = 'Check my calendar'
export const IMPROVE_TWIN_PERFORMANCE_SUGGESTION = `How do I improve my twin's performance?`

export const defaultSuggestedQuestions = [
  SUMMARIZE_EMAIL_SUGGESTION,
  CHECK_CALENDAR_SUGGESTION,
  IMPROVE_TWIN_PERFORMANCE_SUGGESTION,
]

function useFetchSuggestedQuestions(twinId: string, twinOrgId?: string) {
  const [activeOrganization] = useActiveOrganization()
  const [suggestedQuestions, setSuggestedQuestions] = useState<string[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useLayoutEffect(() => {
    async function fetchData() {
      if ((!activeOrganization && !twinOrgId) || !twinId) {
        return
      }

      setIsLoading(true)

      try {
        let response
        if (activeOrganization) {
          response = await twinsSuggestedQuestions(activeOrganization, twinId)
        } else if (twinOrgId) {
          response = await twinsSuggestedQuestions(twinOrgId, twinId)
        }
        const questions = response?.data ? response.data : []

        setSuggestedQuestions(questions)
      } catch (error) {
        setError(`${error}`)
      } finally {
        setIsLoading(false)
      }
    }

    fetchData()
  }, [activeOrganization, setSuggestedQuestions, twinId, twinOrgId])

  return {
    isLoading,
    suggestedQuestions,
    error,
    setSuggestedQuestions,
  }
}

function useFetchQuestionTypeInstructions(twinId: string) {
  const [activeOrganization] = useActiveOrganization()
  const [questionTypeInstructions, setQuestionTypeInstructions] =
    useRecoilState<TwinInstruction[]>(questionTypeInstructionsAtom)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useLayoutEffect(() => {
    async function fetchData() {
      if (!activeOrganization || !twinId) {
        return
      }

      setIsLoading(true)

      try {
        const response = await twinsListInstructions(activeOrganization, twinId, {
          type: TwinInstructionType.question,
        })

        setQuestionTypeInstructions(response.data ? response.data : [])
      } catch (error) {
        setError(`${error}`)
      } finally {
        setIsLoading(false)
      }
    }

    fetchData()
  }, [activeOrganization, setQuestionTypeInstructions, twinId])

  return {
    isLoading,
    questionTypeInstructions,
    error,
    setQuestionTypeInstructions,
  }
}

function useFetchInstructions(twinId: string) {
  const [activeOrganization] = useActiveOrganization()
  const [instructions, setInstructions] = useRecoilState<TwinInstruction[]>(instructionsAtom)

  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useLayoutEffect(() => {
    async function fetchData() {
      if (!activeOrganization || !twinId) {
        return
      }

      setIsLoading(true)

      try {
        const response = await twinsListInstructions(activeOrganization, twinId)

        setInstructions(response.data ? response.data : [])
      } catch (error) {
        setError(`${error}`)
      } finally {
        setIsLoading(false)
      }
    }

    fetchData()
  }, [activeOrganization, setInstructions, twinId])

  return {
    isLoading,
    instructions,
    error,
    setInstructions,
  }
}

export { useFetchInstructions, useFetchSuggestedQuestions, useFetchQuestionTypeInstructions }
